import { useEffect, useState, useCallback } from "react";
import { BridgeEvent, NativeBridgeAvailable } from "@unu/ui-enums";
import { sendEvent, subscribe, unsubscribe } from "@unu/ui-common";
import {
  submitMobileHealthData,
  getMemberProfile,
} from "@/services/core-api-adapter";
import type {
  HealthRecordDTO,
  SubmitHealthRecordResponse,
} from "@/services/core-api-adapter";
import { persistValue, retrieveValue, clearValue } from "@/store/persistence";
import { getNativeAppVersion } from "@/services/native-app-version";
import { trackEvent } from "@/services/analytics-adapter";
import { debugLogger } from "@/utils";
import {
  resolveMetricType,
  REQUIRED_SDK_DATA_TYPES,
} from "@/health/healthDataTypes";

import type { HealthRecord } from "./useNativeHealthSDK";

const SYNC_CONTEXT = "health-data-sync";
const LAST_SYNC_TIMESTAMP_KEY = "healthDataLastSyncTimestamp";
const LEGACY_PENDING_PAYLOAD_KEY = "pendingHealthPayload";
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const FETCH_WINDOW_DAYS = 7;
const DEFAULT_SYNC_THRESHOLD_MINUTES = 5;
const CLOCK_SKEW_BUFFER_MS = 30_000;
const SYNC_REQUESTED_EVENT = "unu:health-data-sync-requested";

let syncInProgress = false;
let legacyCleared = false;

/** Resets module-level sync guards. Test isolation only. */
export function __resetSyncState(): void {
  syncInProgress = false;
  legacyCleared = false;
}

/**
 * Reads `VITE_APP_HEALTHKIT_SYNC_MIN` (minutes) and returns the submit-throttle
 * threshold in milliseconds. Unset, malformed, zero, or negative values fall
 * back to the 5-minute default.
 */
export function getSyncThresholdMs(): number {
  const raw = import.meta.env.VITE_APP_HEALTHKIT_SYNC_MIN;
  if (raw === undefined || raw === null || raw === "") {
    return DEFAULT_SYNC_THRESHOLD_MINUTES * 60_000;
  }
  const parsed = Number.parseInt(String(raw), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_SYNC_THRESHOLD_MINUTES * 60_000;
  }
  return parsed * 60_000;
}

interface HealthDataBridgeResponse {
  records?: HealthRecord[];
  error?: { code: string; message: string };
}

export interface UseHealthDataSyncResult {
  isSyncing: boolean;
  lastSyncTimestamp: string | null;
  lastSyncError: string | null;
  requestSync: () => void;
}

/**
 * Dispatches a custom event to trigger an immediate sync cycle,
 * bypassing the rate limit. Used after permission grants.
 */
export function requestImmediateSync(): void {
  document.dispatchEvent(new CustomEvent(SYNC_REQUESTED_EVENT));
}

/**
 * Transforms all health records to backend DTO format.
 * Each record is sent individually using its sourceId as the metricId
 * for idempotent backend upserts.
 */
export function transformRecordsToPayload(
  records: HealthRecord[]
): HealthRecordDTO[] {
  const metrics: HealthRecordDTO[] = [];

  for (const record of records) {
    const metricType = resolveMetricType(record.dataType);
    if (!metricType) {
      continue;
    }

    metrics.push({
      metricId: record.sourceId,
      metricType,
      value: record.value,
      unit: record.unit,
      status: null,
      timestamp: record.timestamp,
      source: record.platform,
      version: 1,
    });
  }

  return metrics;
}

function getErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === "object") {
    const e = error as Record<string, unknown>;
    const status = e.status ?? (e.response as Record<string, unknown>)?.status;
    return typeof status === "number" ? status : undefined;
  }
  return undefined;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "Sync failed";
}

/**
 * Submits payload with exponential backoff retry
 *
 * @returns Response object on success, null after all retries exhausted
 */
export async function submitWithRetry(
  payload: HealthRecordDTO[],
  maxRetries = MAX_RETRIES
): Promise<SubmitHealthRecordResponse | null> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await submitMobileHealthData(payload);
      return response;
    } catch (error: unknown) {
      const status = getErrorStatus(error);
      if (status && status >= 400 && status < 500) {
        console.error("Health data submission rejected by the server", error, {
          phase: "submitWithRetry",
          reason: "client_error",
          httpStatus: status,
          payloadSize: payload.length,
          dataTypes: Array.from(new Set(payload.map((r) => r.metricType))),
        });
        return null;
      }

      lastError = error;

      if (attempt < maxRetries) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  console.error("Health data submission failed after retries", lastError, {
    phase: "submitWithRetry",
    reason: "retries_exhausted",
    maxRetries,
    payloadSize: payload.length,
    dataTypes: Array.from(new Set(payload.map((r) => r.metricType))),
  });

  return null;
}

export function getLastSyncTimestamp(): string | null {
  return retrieveValue(LAST_SYNC_TIMESTAMP_KEY);
}

export function setLastSyncTimestamp(timestamp: string): void {
  persistValue(LAST_SYNC_TIMESTAMP_KEY, timestamp);
}

export function computeMaxTimestamp(records: HealthRecordDTO[]): string {
  if (records.length === 0) {
    return "";
  }
  return records.reduce<string>(
    (max, r) => (r.timestamp > max ? r.timestamp : max),
    records[0].timestamp
  );
}

/**
 * Fetches the lastDataSync timestamp from the member profile.
 * Returns null if the call fails or the field is not present.
 */
async function fetchProfileLastDataSync(): Promise<string | null> {
  try {
    const profile = await getMemberProfile();
    const ts = profile?.lastDataSync;
    if (ts && !Number.isNaN(Date.parse(ts))) {
      return ts;
    }
    return null;
  } catch (error: unknown) {
    // A failed profile fetch degrades to "no known last-sync timestamp" rather
    // than failing the sync — but it must leave a trace. With no error monitoring
    // in the app, an empty catch here makes a persistently failing profile call
    // invisible in every environment.
    //
    // Log the rejected value as-is: getMemberProfile rejects with `error.data`
    // (a response body or undefined), never an Error, so narrowing on
    // `instanceof Error` here would discard every real failure.
    console.warn(
      "Could not read lastDataSync from the member profile; falling back to the stored timestamp",
      error,
      { phase: "fetchProfileLastDataSync", reason: "profile_fetch_failed" }
    );
    return null;
  }
}

/**
 * Fetches health data from native bridge and returns records via Promise
 *
 * @param sinceTimestamp - ISO string; only fetch records newer than this
 */
function fetchHealthDataViaBridge(
  sinceTimestamp?: string
): Promise<HealthRecord[]> {
  return new Promise((resolve) => {
    const cleanupSubscriptions = () => {
      unsubscribe({
        name: BridgeEvent.HealthDataReceived,
        context: SYNC_CONTEXT,
      });
      unsubscribe({ name: BridgeEvent.HealthError, context: SYNC_CONTEXT });
    };

    const timeout = setTimeout(() => {
      cleanupSubscriptions();
      console.error("Bridge timeout: native shell did not respond", null, {
        phase: "fetchHealthDataViaBridge",
        reason: "bridge_timeout",
        timeoutMs: 30000,
        sinceTimestamp: sinceTimestamp ?? null,
      });
      resolve([]);
    }, 30000);

    subscribe({
      name: BridgeEvent.HealthDataReceived,
      context: SYNC_CONTEXT,
      callback: (data: HealthDataBridgeResponse) => {
        clearTimeout(timeout);
        cleanupSubscriptions();

        if (data?.error) {
          console.error(`Bridge error: ${data.error.code}`, null, {
            phase: "fetchHealthDataViaBridge",
            reason: "bridge_error_response",
            errorCode: data.error.code,
            errorMessage: data.error.message,
            hasRecords: Boolean(data.records?.length),
          });
        }

        const records =
          data?.records && Array.isArray(data.records) ? data.records : [];
        resolve(records);
      },
    });

    subscribe({
      name: BridgeEvent.HealthError,
      context: SYNC_CONTEXT,
      callback: (data: any) => {
        clearTimeout(timeout);
        cleanupSubscriptions();
        console.error(`Bridge error: ${data?.code || "UNKNOWN"}`, null, {
          phase: "fetchHealthDataViaBridge",
          reason: "bridge_error_event",
          errorCode: data?.code,
          errorMessage: data?.message,
        });
        resolve([]);
      },
    });

    const endDate = new Date();
    let startDate: Date;

    if (sinceTimestamp) {
      startDate = new Date(
        new Date(sinceTimestamp).getTime() - CLOCK_SKEW_BUFFER_MS
      );
    } else {
      startDate = new Date();
      startDate.setDate(startDate.getDate() - FETCH_WINDOW_DAYS);
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthFetchData,
        data: {
          dataTypes: [...REQUIRED_SDK_DATA_TYPES],
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      },
    });
  });
}

/**
 * useHealthDataSync
 *
 * Syncs health data from native HealthKit/Health Connect to the backend.
 * Pull-based: triggered only by explicit route-focus `requestSync()` calls
 * or the `SYNC_REQUESTED_EVENT` (post-permission grant). Every trigger
 * pulls + transforms; the backend submit is gated by a configurable
 * `VITE_APP_HEALTHKIT_SYNC_MIN` threshold (default 5 minutes) comparing
 * now against `lastBackendSync` from localStorage.
 */
export function useHealthDataSync(): UseHealthDataSyncResult {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTimestamp, setLastSyncTimestampState] = useState<
    string | null
  >(null);
  const [lastSyncError, setLastSyncError] = useState<string | null>(null);

  const runSync = useCallback(async () => {
    if (!NativeBridgeAvailable) {
      return;
    }

    // Gates the entire health-sync path: without a version reported by the
    // native shell there is nothing to sync from.
    if (!getNativeAppVersion()) {
      return;
    }

    const authResult = retrieveValue("authenticationResult");
    if (!authResult) {
      return;
    }

    if (syncInProgress) {
      return;
    }
    syncInProgress = true;
    setIsSyncing(true);
    setLastSyncError(null);

    let syncRunContext: {
      platform: string | null;
      recordCount: number;
      payloadCount: number;
      success: boolean;
    } = { platform: null, recordCount: 0, payloadCount: 0, success: false };

    try {
      if (!legacyCleared) {
        clearValue(LEGACY_PENDING_PAYLOAD_KEY);
        legacyCleared = true;
      }

      const profileTimestamp = await fetchProfileLastDataSync();
      const storedTimestamp = getLastSyncTimestamp();
      const sinceTimestamp = profileTimestamp ?? storedTimestamp ?? undefined;

      const records = await fetchHealthDataViaBridge(sinceTimestamp);

      if (records.length === 0) {
        return;
      }

      const platform = records[0]?.platform ?? null;
      syncRunContext = {
        ...syncRunContext,
        recordCount: records.length,
        platform,
      };

      const payload = transformRecordsToPayload(records);

      if (payload.length === 0) {
        return;
      }

      syncRunContext.payloadCount = payload.length;

      debugLogger.healthSync("DTO payload to BE", { payload });

      // Gate only submit by threshold. lastBackendSync === null => always submit.
      const lastBackendSync = storedTimestamp;
      if (lastBackendSync !== null) {
        const lastSyncMs = Date.parse(lastBackendSync);
        const threshold = getSyncThresholdMs();
        if (!Number.isNaN(lastSyncMs) && Date.now() - lastSyncMs < threshold) {
          return;
        }
      }

      const response = await submitWithRetry(payload);

      const success = response && !response.isError;
      syncRunContext.success = !!success;

      if (success) {
        const newTimestamp =
          response.lastDataSyncTimestamp || computeMaxTimestamp(payload);

        if (newTimestamp) {
          setLastSyncTimestamp(newTimestamp);
          setLastSyncTimestampState(newTimestamp);
        }
      } else if (response?.isError) {
        console.error("Backend rejected health data submission", null, {
          phase: "runSync",
          reason: "backend_rejected",
          backendMessage: response.message ?? null,
          payloadSize: payload.length,
          dataTypes: Array.from(new Set(payload.map((r) => r.metricType))),
        });
        setLastSyncError(response.message || "Backend rejected health data");
      } else {
        console.error(
          "Health data submission produced no usable response",
          null,
          {
            phase: "runSync",
            reason: "no_response_body",
            payloadSize: payload.length,
            dataTypes: Array.from(new Set(payload.map((r) => r.metricType))),
          }
        );
        setLastSyncError(
          response?.message || "Failed to send health data after retries"
        );
      }
    } catch (error: unknown) {
      console.error("Health sync failed", error, { phase: "runSync" });
      setLastSyncError(getErrorMessage(error));
    } finally {
      syncInProgress = false;
      setIsSyncing(false);

      if (syncRunContext.recordCount > 0) {
        trackEvent({
          event: "health_sync_completed",
          platform: syncRunContext.platform ?? "unknown",
          recordCount: syncRunContext.recordCount,
          payloadCount: syncRunContext.payloadCount,
          success: syncRunContext.success,
        });
      }
    }
  }, []);

  // Listener for the device-link / post-permission-grant immediate sync event.
  // Kept as its own effect (separate from any pull-based trigger) so the
  // listener lifecycle is independent and obvious.
  useEffect(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    const handleImmediateSyncRequest = () => {
      runSync();
    };

    document.addEventListener(SYNC_REQUESTED_EVENT, handleImmediateSyncRequest);

    return () => {
      document.removeEventListener(
        SYNC_REQUESTED_EVENT,
        handleImmediateSyncRequest
      );
    };
  }, [runSync]);

  return {
    isSyncing,
    lastSyncTimestamp,
    lastSyncError,
    requestSync: runSync,
  };
}

export default useHealthDataSync;
